import { Suspense } from "react";

import { FilterBarRow, PendoId, PendoProps } from "@impero-com/ui";
import { Select } from "antd";

import * as theme from "./FilterTypeRow.css";

interface FilterTypeRowProps<T extends string> extends PendoProps {
    removeButtonPendoId?: PendoId;
    children: React.ReactNode;
    filterType: T;
    index: number;
    enabledFilterTypes: T[];
    selectedFilterTypes: T[];
    localizeFilterType: (filterType: T) => string;
    onFilterRemove: (filterType: T) => void;
    onFilterTypeChange: (filterType: T, index: number) => void;
}

/**
 * One row of a filter panel: the filter-type dropdown, the filter itself and the
 * remove button. Shared by the dashboard and Control Health panels.
 */
export function FilterTypeRow<T extends string>({
    children,
    filterType,
    index,
    enabledFilterTypes,
    selectedFilterTypes,
    localizeFilterType,
    onFilterRemove,
    onFilterTypeChange,
    "data-pendo": dataPendo,
    removeButtonPendoId,
}: FilterTypeRowProps<T>) {
    const options = enabledFilterTypes.map((enabledFilterType) => ({
        label: localizeFilterType(enabledFilterType),
        value: enabledFilterType,
        disabled: selectedFilterTypes.includes(enabledFilterType),
    }));

    return (
        <FilterBarRow
            data-pendo={dataPendo}
            removeButtonPendoId={removeButtonPendoId}
            onFilterRemoveClick={() => onFilterRemove(filterType)}
            columnOptions={options}
            selectedColumnOption={filterType}
            onColumnOptionChange={(value) =>
                onFilterTypeChange(value as T, index)
            }
        >
            <Suspense fallback={<SelectSkeleton />}>{children}</Suspense>
        </FilterBarRow>
    );
}

function SelectSkeleton() {
    return (
        <Select loading disabled mode="multiple" className={theme.skeleton} />
    );
}
